import { Box, Stack } from "@mui/material";
import { DoughnutCard } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Product } from "src/api/client-api/products/useGetOneProduct";
import { ProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";
import { type MainScore, OlapScoresResult } from "src/api/client-api/olapScores/olapScoresSchema";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useProductScoresContext } from "src/context/Product/Scores/useProductScores";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ScoreSelectHeader } from "./ScoreSelectHeader";
import { TotalScoreCard } from "./TotalScoreCard";
import { LifeCycleStagesCard } from "./LifeCycleStagesCard";
import type { ProductFormScoresMode } from "../scores/useProductFormScores";
import type { ScoreWithVariation } from "src/services/ScoreService";
import type { ProductReferenceScoresMap } from "src/api/client-api/products/useProductLivePreview";

const resolveReferenceScore = (scores: ProductReferenceScoresMap, scoreId: number): number | undefined => {
    const value = scores[String(scoreId)];

    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

type ScoreLiveSidebarProps = {
    existingProduct: Product | undefined;
    product: ProductFormType;
    productReference: ProductReference | undefined;
    productReferenceScores: ProductReferenceScoresMap;
    mode: ProductFormScoresMode;
    olapScores: OlapScoresResult;
    isLoading?: boolean;
    scoreQueryError?: unknown;
    retryScoreQuery?: () => Promise<unknown> | void;
    stickyTop: number;
};

/**
 * Sticky right-column score sidebar of the V2 long-scroll product form.
 * Persisted OLAP while preview data is unavailable; calculate-preview becomes the
 * source of truth as soon as its response is ready.
 */
export const ScoreLiveSidebar = (props: ScoreLiveSidebarProps) => {
    const {
        existingProduct,
        productReference,
        productReferenceScores,
        mode,
        olapScores,
        isLoading = false,
        scoreQueryError,
        retryScoreQuery,
        stickyTop,
    } = props;
    const { t } = useTranslation();
    const { lastSelectedVariation, selectedScorePrecision } = useEntityScores();

    return (
        <Box
            sx={(theme) => ({
                position: "sticky",
                top: stickyTop,
                width: "100%",
                minWidth: 0,
                maxWidth: theme.custom.doughnutCard.width,
                "& > .MuiStack-root": {
                    width: "100%",
                    minWidth: 0,
                },
            })}
            aria-label={t("product.v2.score_live.aria", "Score tracker")}
        >
            <DoughnutCard>
                {mode === "persisted" && existingProduct !== undefined ?
                    <ScoreLiveSidebarPersistedContent
                        productReference={productReference}
                        productReferenceScores={productReferenceScores}
                        persistedOlapScores={olapScores}
                        savedProductOlapScores={existingProduct.olapScores ?? {}}
                        selectedScore={lastSelectedVariation}
                        selectedScorePrecision={selectedScorePrecision}
                    />
                :   <ScoreLiveSidebarPreview
                        productReference={productReference}
                        productReferenceScores={productReferenceScores}
                        previewOlapScores={olapScores}
                        isPreviewLoading={isLoading}
                        scoreQueryError={scoreQueryError}
                        retryScoreQuery={retryScoreQuery}
                        selectedScore={lastSelectedVariation}
                        selectedScorePrecision={selectedScorePrecision}
                    />
                }
            </DoughnutCard>
        </Box>
    );
};

type PreviewProps = {
    productReference: ProductReference | undefined;
    productReferenceScores: ProductReferenceScoresMap;
    previewOlapScores: OlapScoresResult;
    isPreviewLoading: boolean;
    scoreQueryError?: unknown;
    retryScoreQuery?: () => Promise<unknown> | void;
    selectedScore: ScoreWithVariation;
    selectedScorePrecision: number;
};

export const resolveSelectedMainScore = (
    mainScore: MainScore | undefined,
    selectedScore: Pick<ScoreWithVariation, "variationId" | "unit">,
): MainScore | undefined => {
    if (mainScore === undefined) {
        return mainScore;
    }

    if (selectedScore.variationId === null) {
        return mainScore.unit === selectedScore.unit ?
                mainScore
            :   { ...mainScore, score: { value: null }, unit: selectedScore.unit };
    }

    const variation = mainScore.variations.find((item) => item.id === selectedScore.variationId);

    if (variation?.score !== undefined) {
        return {
            ...mainScore,
            score: variation.score,
            unit: variation.unit,
        };
    }

    if (mainScore.unit === selectedScore.unit) {
        return mainScore;
    }

    return {
        ...mainScore,
        score: { value: null },
        unit: selectedScore.unit,
    };
};

const ScoreLiveSidebarPreview = (props: PreviewProps) => {
    const {
        productReference,
        productReferenceScores,
        previewOlapScores,
        isPreviewLoading,
        scoreQueryError,
        retryScoreQuery,
        selectedScore,
        selectedScorePrecision,
    } = props;
    const previewMainScore = previewOlapScores[selectedScore.scoreId.toString()] as MainScore | undefined;
    const mainScore = resolveSelectedMainScore(previewMainScore, selectedScore);

    return (
        <ScoreLiveSidebarContent
            productReference={productReference}
            productReferenceScores={productReferenceScores}
            mainScore={mainScore}
            selectedScore={selectedScore}
            selectedScorePrecision={selectedScorePrecision}
            isLoading={isPreviewLoading}
            scoreQueryError={scoreQueryError}
            retryScoreQuery={retryScoreQuery}
        />
    );
};

type PersistedContentProps = {
    productReference: ProductReference | undefined;
    productReferenceScores: ProductReferenceScoresMap;
    persistedOlapScores: OlapScoresResult;
    savedProductOlapScores: OlapScoresResult;
    selectedScore: ScoreWithVariation;
    selectedScorePrecision: number;
};

const ScoreLiveSidebarPersistedContent = (props: PersistedContentProps) => {
    const {
        productReference,
        productReferenceScores,
        persistedOlapScores,
        savedProductOlapScores,
        selectedScore,
        selectedScorePrecision,
    } = props;
    const { scores, areScoresRefetching, scoreQueryError, retryScoreQuery } = useProductScoresContext();

    const selectedScoreKey = selectedScore.scoreId.toString();
    const persistedMainScore =
        scores?.olapScores[selectedScoreKey] ??
        persistedOlapScores[selectedScoreKey] ??
        savedProductOlapScores[selectedScoreKey];
    const mainScore = resolveSelectedMainScore(persistedMainScore, selectedScore);

    return (
        <ScoreLiveSidebarContent
            productReference={productReference}
            productReferenceScores={productReferenceScores}
            mainScore={mainScore}
            selectedScore={selectedScore}
            selectedScorePrecision={selectedScorePrecision}
            isLoading={areScoresRefetching}
            scoreQueryError={scoreQueryError}
            retryScoreQuery={retryScoreQuery}
        />
    );
};

type ScoreLiveSidebarContentProps = {
    productReference: ProductReference | undefined;
    productReferenceScores: ProductReferenceScoresMap;
    mainScore: MainScore | undefined;
    selectedScore: ScoreWithVariation;
    selectedScorePrecision: number;
    isLoading: boolean;
    scoreQueryError?: unknown;
    retryScoreQuery?: () => Promise<unknown> | void;
};

const ScoreLiveSidebarContent = (props: ScoreLiveSidebarContentProps) => {
    const {
        productReference,
        productReferenceScores,
        mainScore,
        selectedScore,
        selectedScorePrecision,
        isLoading,
        scoreQueryError,
        retryScoreQuery,
    } = props;
    const hasComputedScore = OlapScoresService.normalizeNumericalScore(mainScore?.score.value, null) !== null;

    const referenceScore =
        productReference === undefined ? undefined : (
            resolveReferenceScore(productReferenceScores, selectedScore.scoreId)
        );

    return (
        <>
            <ScoreSelectHeader />
            <Stack sx={(theme) => ({ width: "100%", padding: theme.spacing(2), gap: theme.spacing(2) })}>
                <TotalScoreCard
                    mainScore={mainScore}
                    unit={selectedScore.unit}
                    precision={selectedScorePrecision}
                    referenceScore={referenceScore}
                    referenceName={productReference?.name}
                    improvement={selectedScore.improvement}
                    isLoading={isLoading}
                    scoreQueryError={scoreQueryError}
                    retryScoreQuery={retryScoreQuery}
                />
                {!isLoading && hasComputedScore && <LifeCycleStagesCard mainScore={mainScore} />}
            </Stack>
        </>
    );
};
